import React from "react";
import lodash from "lodash";
import T from "prop-types";
import { connect } from "react-redux";
import { Field, FieldArray } from "redux-form";
import { Button, Col, Row } from "react-bootstrap";
import { validator } from "LIB/validate-this";
import * as rules from "LIB/validation/rules";
import { Icon, ExternalLink } from "MODULES/shared/components";
import {
  form,
  Form as AppForm,
  Check,
  CheckGroup,
  FormInput,
  FormRow
} from "MODULES/shared/components/Forms";
import {
  AS_REMOVED,
  ATTACH_TAG_COPIES,
  ATTACH_TRACEABILITY,
  AWAITING_INFO_TO_COMPLETE,
  OFFER,
  OUTGOING_LINE_ITEMS
} from "MODULES/OutgoingQuotes/constants";
import { properCase } from "LIB/formatters/text";
import { ATTACHMENTS, EMAIL, ID, LINE_ITEMS } from "APP_ROOT/constants";
import { REPAIR_AND_HOLD } from "MODULES/RepairOrders/constants";
import { REPAIRING } from "MODULES/inventories/constants";
import { CONTACT } from "MODULES/companies/constants";
import SelectAttachmentTable from "MODULES/OutgoingQuotes/Detail/Send/SelectAttachmentTable";
import resourceToStateKey from "MODULES/shared/components/Notes/resourceToStateKey";
import resourceTypes from "MODULES/shared/types/resourceTypes";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import SelectImages from "MODULES/parts/components/Detail/OutgoingQuotes/SelectImages";
import { QUOTE_TYPE } from "MODULES/RepairQuotes/constants";
import { sendEmail } from "MODULES/OutgoingQuotes/actions";

const FORM_NAME = "send-outgoing-quote";

const excludedQuoteTypes = [AWAITING_INFO_TO_COMPLETE, OFFER];

function filteredLineItems(quote) {
  return quote
    .get("outgoing_quote_line_items")
    .toJS()
    .filter(i => !excludedQuoteTypes.includes(lodash.get(i, QUOTE_TYPE)));
}

function LineItems({ fields, lineItems, smallForm }) {
  return (
    <tbody>
      {fields.map((item, idx) => {
        const lineItem = lineItems[idx];
        const traceability = lineItem.traceability_attachment;
        const tagCopies =
          lineItem.tag_copies_attachment &&
          lineItem.inventory.repair_status !== REPAIR_AND_HOLD &&
          lineItem.inventory.status !== REPAIRING;

        return (
          <tr key={`item-${lineItem.id}`}>
            <td
              className="text-right"
              style={{ verticalAlign: "top", paddingTop: 8, paddingLeft: 10 }}
            >
              {idx + 1}
            </td>
            <td>
              <Row>
                <Col md={12}>
                  <span className="float-left">
                    {lineItem.part.part_number} | Serial #&nbsp;
                    {lineItem.serial_number} |&nbsp;
                    {OUTGOING_LINE_ITEMS.quoteType[lineItem.quote_type].label}
                  </span>
                </Col>
              </Row>
              {!smallForm && [
                !traceability &&
                  !tagCopies && (
                    <FormRow>
                      <Col md={12}>
                        <em>No attachments available for this item.</em>
                      </Col>
                    </FormRow>
                  ),
                <FormRow>
                  {(traceability || tagCopies) && (
                    <Col md={6}>
                      <CheckGroup title="Attachments" justified>
                        {traceability && (
                          <Field
                            name={`${item}.${ATTACH_TRACEABILITY}`}
                            component={Check}
                            label={properCase(ATTACH_TRACEABILITY)}
                          />
                        )}
                        {tagCopies && (
                          <Field
                            name={`${item}.${ATTACH_TAG_COPIES}`}
                            component={Check}
                            label={properCase(ATTACH_TAG_COPIES)}
                          />
                        )}
                      </CheckGroup>
                    </Col>
                  )}
                  <Col md={3}>
                    <CheckGroup title="Hazmat" justified>
                      <Field
                        name={`${item}.show_hazmat_warning`}
                        component={Check}
                        label="Show Hazmat Warning"
                      />
                    </CheckGroup>
                  </Col>
                </FormRow>
              ]}
              {smallForm && (
                <FormRow>
                  {!traceability &&
                    !tagCopies && (
                      <Col md={12}>
                        <em>No attachments available for this item.</em>
                      </Col>
                    )}
                  {(traceability || tagCopies) && (
                    <Col md={9}>
                      <CheckGroup title="Attachments" justified>
                        {traceability && (
                          <Field
                            name={`${item}.${ATTACH_TRACEABILITY}`}
                            component={Check}
                            label={properCase(ATTACH_TRACEABILITY)}
                          />
                        )}
                        {tagCopies && (
                          <Field
                            name={`${item}.${ATTACH_TAG_COPIES}`}
                            component={Check}
                            label={properCase(ATTACH_TAG_COPIES)}
                          />
                        )}
                      </CheckGroup>
                    </Col>
                  )}
                  <Col md={4}>
                    <CheckGroup title="Hazmat" justified>
                      <Field
                        name={`${item}.show_hazmat_warning`}
                        component={Check}
                        label="Show Hazmat"
                      />
                    </CheckGroup>
                  </Col>
                </FormRow>
              )}
              {traceability &&
                tagCopies && (
                  <FormRow>
                    {traceability && (
                      <Col md={3} style={{ marginTop: 0 }}>
                        <ExternalLink url={traceability.public_download_url}>
                          Download Traceability
                        </ExternalLink>
                      </Col>
                    )}
                    {tagCopies && (
                      <Col md={3} style={{ marginTop: 0 }}>
                        <ExternalLink url={tagCopies.public_download_url}>
                          Download Tag Copies
                        </ExternalLink>
                      </Col>
                    )}
                  </FormRow>
                )}
              <FormRow>
                <Col md={12}>
                  <SelectImages
                    formSection={item}
                    formName={FORM_NAME}
                    inventory={lineItem.inventory}
                  />
                </Col>
              </FormRow>
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}

LineItems.propTypes = {
  lineItems: T.instanceOf(Array),
  fields: T.shape({
    map: T.func
  }),
  smallForm: T.bool
};

export function Form({
  attachments,
  close,
  getFieldValue,
  handleSubmit,
  invalid,
  setFieldValue,
  submitting,
  quote,
  modal,
  smallForm
}) {
  return (
    <AppForm handleSubmit={handleSubmit} modal={modal}>
      <AppForm.Body>
        <Field component={FormInput} name="to" label="To:" required />
        <Field component={FormInput} name="cc" label="CC:" />
        <Field component={FormInput} name="bcc" label="BCC:" />
        <div>
          <h4>Attachments</h4>
          <FormRow>
            <Col md={12}>
              <table className="table table-striped table-numbered margin-bottom-none">
                <FieldArray
                  name={LINE_ITEMS}
                  component={LineItems}
                  lineItems={filteredLineItems(quote)}
                  smallForm={smallForm}
                />
              </table>
            </Col>
          </FormRow>
        </div>
        {attachments &&
          attachments.length > 0 && (
            <FormRow>
              <Col md={12}>
                <SelectAttachmentTable
                  attachments={attachments}
                  getFieldValue={getFieldValue}
                  setFieldValue={setFieldValue}
                />
              </Col>
            </FormRow>
          )}
      </AppForm.Body>
      <AppForm.Footer>
        <Button onClick={close}>Cancel</Button>
        <Button
          type="submit"
          bsStyle="primary"
          disabled={invalid || submitting}
        >
          <Icon name="envelope" fixed />
          &nbsp;{submitting ? "Sending..." : "Send Quote"}
        </Button>
      </AppForm.Footer>
    </AppForm>
  );
}

Form.propTypes = {
  attachments: T.instanceOf(Array),
  close: T.func.isRequired,
  getFieldValue: T.func.isRequired,
  handleSubmit: T.func.isRequired,
  invalid: T.bool.isRequired,
  setFieldValue: T.func.isRequired,
  submitting: T.bool.isRequired,
  quote: T.shape({
    get: T.func,
    getIn: T.func
  }),
  modal: T.bool,
  smallForm: T.bool
};

export default connect(
  (state, props) => ({
    attachments: state[resourceToStateKey(resourceTypes.outgoingQuote)]
      .get(ATTACHMENTS)
      .toJS(),
    initialValues: {
      attachment_ids: [],
      to: props.quote.getIn([CONTACT, EMAIL]),
      bcc: props.currentUser.email,
      line_items: filteredLineItems(props.quote).map(item => ({
        id: item.id,
        attach_traceability: false,
        attach_tag_copies:
          !!item.tag_copies_attachment &&
          lodash.get(item, "inventory.condition") !== AS_REMOVED,
        inventory_attachment_ids: [],
        show_hazmat_warning: lodash.get(item, "part.hazmat")
      }))
    }
  }),
  (dispatch, ownProps) => ({
    onSubmit: data =>
      dispatch(sendEmail(ownProps.quote.get(ID), data, ownProps.close))
  })
)(
  form({
    form: FORM_NAME,
    validate: values =>
      validator(values, f => {
        f.validate("body", "to").satisfies(rules.required);
        f.validate("to", "cc", "bcc").satisfies(rules.isValidEmail);
      })
  })(withFieldAccessors(Form))
);



// WEBPACK FOOTER //
// ./app/modules/OutgoingQuotes/Detail/Send/Form.jsx